import { redactPaymentLinkText } from './redact-sensitive-data';
import { redactJotformText, isJotformSensitiveKey } from './jotform-privacy';

/**
 * Bounded, JSON-safe, credential-redacted value sanitization.
 *
 * Deliberately dependency-free so boot-time capture (device diagnostic
 * logging) can use it without pulling Firebase, stores, or config into its
 * import graph. Extracted from the device console-log uploader, which still
 * re-exports it under its original name.
 *
 * The bounds exist because diagnostic payloads are attacker-shaped by
 * accident: a cyclic store snapshot, a 40k-element array, or a base64 blob
 * must degrade into a marker rather than hang the caller or blow the budget.
 */

export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };

export const BOUNDED_SANITIZE_MAX_STRING_LENGTH = 2_000;
export const BOUNDED_SANITIZE_MAX_DEPTH = 8;
export const BOUNDED_SANITIZE_MAX_KEYS_PER_OBJECT = 100;
export const BOUNDED_SANITIZE_MAX_ARRAY_ITEMS = 100;
export const BOUNDED_SANITIZE_MAX_NODES = 12_000;

const REDACTED = '[redacted]';
const TRUNCATED = '[truncated]';

const SENSITIVE_KEY_FRAGMENTS = [
  'apikey',
  'authorization',
  'bearertoken',
  'credential',
  'password',
  'secret',
  'signingkey',
  'privatekey',
  'token',
];
const SENSITIVE_EXACT_KEYS = new Set([
  'invitation',
  'invitationhash',
  'pairingcode',
  'pc',
  'cardnumber',
  'pan',
  'track',
  'track1',
  'track2',
  'xpointcraftoperatorsession',
]);
const BEARER_PATTERN = /bearer\s+[a-z0-9._~+/=-]+/gi;

interface SanitizationState {
  remainingNodes: number;
  seen: WeakSet<object>;
  /** Extra per-string scrubbing, applied before the length cap. */
  transformString?: (value: string) => string;
}

export type BoundedSanitizeOptions = {
  /**
   * Applied to every string encountered, before truncation. Callers that need
   * pattern-based scrubbing (tokens pasted into a message) pass it here rather
   * than post-processing the serialized result, which would risk producing
   * invalid JSON.
   */
  transformString?: (value: string) => string;
};

export function isBoundedSanitizeSensitiveKey(key: string): boolean {
  const normalized = key.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (SENSITIVE_EXACT_KEYS.has(normalized) || isJotformSensitiveKey(key)) return true;
  return SENSITIVE_KEY_FRAGMENTS.some((fragment) => normalized.includes(fragment));
}

/** A typed presence flag describes a credential; it does not contain one. */
export function isDiagnosticPresenceFlag(key: string, value: unknown): value is boolean {
  return typeof value === 'boolean' && /(?:present|configured|available)$/i.test(key);
}

/** Keep existing masks and the useful last four, never an unmasked card field. */
export function redactDiagnosticCardNumber(value: unknown): string {
  const text = String(value);
  if (/^\[pan:\d{4}\]$/.test(text) || /^[*xX• -]+\d{4}$/.test(text) || /^\d{4}$/.test(text))
    return text;
  const digits = text.replace(/[ -]/g, '');
  return /^\d{13,19}$/.test(digits) ? `[pan:${digits.slice(-4)}]` : REDACTED;
}

function sanitizeString(value: string, state: SanitizationState): string {
  let scrubbed = redactPaymentLinkText(redactJotformText(value, REDACTED), REDACTED).replace(
    BEARER_PATTERN,
    'Bearer [redacted]'
  );
  if (state.transformString) {
    try {
      scrubbed = state.transformString(scrubbed);
    } catch {
      return REDACTED;
    }
  }
  return scrubbed.length > BOUNDED_SANITIZE_MAX_STRING_LENGTH
    ? `${scrubbed.slice(0, BOUNDED_SANITIZE_MAX_STRING_LENGTH)}…`
    : scrubbed;
}

function sanitizeValue(
  value: unknown,
  depth: number,
  state: SanitizationState
): JsonValue | undefined {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (state.remainingNodes <= 0) return TRUNCATED;
  state.remainingNodes -= 1;

  if (typeof value === 'string') return sanitizeString(value, state);
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return Number.isFinite(value) ? value : String(value);
  if (typeof value === 'bigint') return value.toString();
  if (typeof value === 'function') return '[function]';
  if (typeof value === 'symbol') return value.toString();
  if (typeof value !== 'object') return String(value);
  if (value instanceof Date) return value.toISOString();
  if (depth >= BOUNDED_SANITIZE_MAX_DEPTH) return '[max-depth]';
  if (state.seen.has(value)) return '[circular]';
  state.seen.add(value);

  if (Array.isArray(value)) {
    const result = value.slice(0, BOUNDED_SANITIZE_MAX_ARRAY_ITEMS).map((item) => {
      const sanitized = sanitizeValue(item, depth + 1, state);
      return sanitized === undefined ? null : sanitized;
    });
    if (value.length > BOUNDED_SANITIZE_MAX_ARRAY_ITEMS) {
      result.push(`[+${value.length - BOUNDED_SANITIZE_MAX_ARRAY_ITEMS} more items]`);
    }
    return result;
  }

  if (value instanceof Error) {
    return {
      name: sanitizeString(value.name || 'Error', state),
      message: sanitizeString(value.message, state),
    };
  }

  const pairs = Object.entries(value as Record<string, unknown>);
  const result: Record<string, JsonValue> = {};
  pairs.slice(0, BOUNDED_SANITIZE_MAX_KEYS_PER_OBJECT).forEach(([key, item]) => {
    if (/^(?:cardnumber|pan)$/.test(key.toLowerCase().replace(/[^a-z0-9]/g, ''))) {
      result[key] = redactDiagnosticCardNumber(item);
      return;
    }
    if (isDiagnosticPresenceFlag(key, item)) {
      result[key] = item;
      return;
    }
    if (isBoundedSanitizeSensitiveKey(key)) {
      result[key] = REDACTED;
      return;
    }
    const sanitized = sanitizeValue(item, depth + 1, state);
    if (sanitized !== undefined) result[key] = sanitized;
  });
  if (pairs.length > BOUNDED_SANITIZE_MAX_KEYS_PER_OBJECT) {
    result['…'] = `[+${pairs.length - BOUNDED_SANITIZE_MAX_KEYS_PER_OBJECT} more keys]`;
  }
  return result;
}

/** Convert arbitrary diagnostic data to bounded, JSON-safe, credential-redacted data. */
export function sanitizeBoundedValue(
  value: unknown,
  options?: BoundedSanitizeOptions
): JsonValue | undefined {
  try {
    return sanitizeValue(value, 0, {
      remainingNodes: BOUNDED_SANITIZE_MAX_NODES,
      seen: new WeakSet<object>(),
      transformString: options?.transformString,
    });
  } catch {
    return '[unserializable]';
  }
}
